import React from "react";
import styled from "@emotion/styled";
import validateCurrencyCode from "validate-currency-code";

// ICU picks the compact suffix from the locale's own data, and that data changes between
// runtime versions: en-GB has rendered 12,345 as both "£12.35K" and "£12.35k", and
// 1,234,567,890 as both "1.2B" and "1.2bn". Pin the suffix so every browser agrees.
const COMPACT_SUFFIXES: Record<string, string | undefined> = {
  k: "K",
  m: "M",
  b: "B",
  bn: "B",
  t: "T",
  tn: "T",
};

// Anything not in the table above (a non-English locale's suffix, say) is left as ICU
// rendered it. Standard notation has no compact part, so this is a no-op there.
const formatWithFixedSuffix = (formatter: Intl.NumberFormat, value: number) =>
  formatter
    .formatToParts(value)
    .map((part) =>
      part.type === "compact"
        ? COMPACT_SUFFIXES[part.value.toLowerCase()] ?? part.value
        : part.value,
    )
    .join("");

/**
 * Smart currency formatter, which changes the format based on the amount.
 * @param value Null = empty amount (e.g. £ --)
 * @param currency An ISO 3166 currency code (e.g. GBP)
 * @param locale If numbers should be formatted using a specific locale
 * @returns A formatted currency string
 */
export function currencyFormat(
  value: number | null, // Not including undefined since components should handle the loading state themselves
  currency: string,
  locale: Intl.LocalesArgument = "en-GB", // Should be changed to undefined eventually
): string {
  try {
    if (currency && !validateCurrencyCode(currency)) {
      console.warn(`Invalid currency code found when formatting: ${currency}`);
      currency = "GBP";
    }

    const formatter = new Intl.NumberFormat(locale, {
      ...(value !== null && Math.abs(value) >= 10000
        ? highCurrencyFormatter
        : lowCurrencyFormatterOpts),
      currency,
    });

    // If no value, show £ --
    if (value === null) {
      return emptyValue(formatter.format(0));
    }
    return formatWithFixedSuffix(formatter, value);
  } catch (e) {
    console.error("Failed to format currency", e);
    return emptyValue(value?.toString() ?? "");
  }
}

/**
 * Currency formatter, which always shows the integer part of the number and depending on the amount the decimal part as well.
 * @param value Null = empty amount (e.g. £ --)
 * @param currency An ISO 3166 currency code (e.g. GBP)
 * @param locale If numbers should be formatted using a specific locale
 * @returns A formatted currency string
 */
export function currencyFormatLong(
  value: number | null, // Not including undefined since components should handle the loading state themselves
  currency: string,
  locale: Intl.LocalesArgument = "en-GB", // Should be changed to undefined eventually
): string {
  try {
    if (currency && !validateCurrencyCode(currency.toUpperCase())) {
      console.warn(`Invalid currency code found when formatting: ${currency}`);
      currency = "GBP";
    }

    // No pennies at any size: mixing £1,234.00 with £12,345 in one right-aligned column
    // puts the digits in different places and makes the values hard to compare.
    const formatter = new Intl.NumberFormat(locale, {
      ...lowCurrencyFormatterOpts,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      currency,
    });

    // If no value, show £ --
    if (value === null) {
      return emptyValue(formatter.format(0));
    }
    return formatter.format(value);
  } catch (e) {
    console.error("Failed to format currency", e);
    return emptyValue(value?.toString() ?? "");
  }
}

/**
 * Shows most of the number, with some minor rounding
 * @param value Number to format
 * @param locale Leave undefined to use the browser's locale
 * @returns String formatted number
 */
export function numberFormatLong(value: number | null, locale?: Intl.LocalesArgument): string {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "decimal",
      notation: "standard",
      maximumFractionDigits: 3, // Default value
    });
    if (value === null) {
      return emptyValue(formatter.format(0));
    }
    return formatter.format(value);
  } catch (e) {
    console.error("Failed to format number", e);
    return emptyValue(value?.toString() ?? "");
  }
}

/**
 *
 * @param value Number to format
 * @param locale Leave undefined to use the browser's locale
 * @returns String formatted number
 */
export function numberFormatShort(value: number | null, locale?: Intl.LocalesArgument): string {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "decimal",
      notation: "compact",
      maximumFractionDigits: value && value > 1000 ? 1 : 0,
      trailingZeroDisplay: "stripIfInteger",
    });
    if (value === null) {
      return emptyValue(formatter.format(0));
    }
    return formatWithFixedSuffix(formatter, value);
  } catch (e) {
    console.error("Failed to format number", e);
    return emptyValue(value?.toString() ?? "");
  }
}

// Turn any form of 0 into an -- string
const emptyValue = (input: string) =>
  // These aren't normal spaces below! They are non-breaking spaces
  // since Intl.NumberFormat will output them.
  input
    .replace("0.00", " -- ")
    .replace("0,00", " -- ")
    .replaceAll("0", " -- ")
    .replaceAll(" ", " ") // The one normal space on the left (since we convert to non-breaking)
    .replaceAll("  ", " ")
    .trim();

// For 9999.99 and below
const lowCurrencyFormatterOpts = {
  style: "currency",
  notation: "standard",
  minimumFractionDigits: 2,
} as Intl.NumberFormatOptions;

// For 10000.00 and above. Always two decimals rather than a fixed number of significant
// digits: it puts the decimal point the same distance from the right edge at every
// magnitude, so right-aligned columns line up (£12.35K / £123.46K / £1.23M).
const highCurrencyFormatter = {
  style: "currency",
  notation: "compact",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} as Intl.NumberFormatOptions;

// Fixed-width digits, so values stack cleanly when several are listed together
const TabularFigures = styled.span({
  fontVariantNumeric: "tabular-nums",
});

/**
 * @deprecated Prefer using format functions directly
 */
export function formatNumber({
  value,
  locale = "en-GB",
  shortFormat,
  currency,
}: {
  value: number | null | undefined;
  locale: string;
  currency?: string;
  shortFormat?: boolean;
}) {
  if (currency) {
    return currencyFormat(value ?? null, currency, locale);
  }
  return shortFormat ? numberFormatShort(value ?? 0, locale) : numberFormatLong(value ?? 0, locale);
}

export const Currency = ({
  value,
  currency = "GBP",
  locale = "en-GB",
  className,
  longFormat,
}: {
  value?: number | null;
  currency?: string;
  locale?: string;
  className?: string;
  longFormat?: boolean;
}) => {
  return (
    <TabularFigures className={className}>
      {longFormat
        ? currencyFormatLong(value !== undefined ? value : 0, currency, locale)
        : currencyFormat(value !== undefined ? value : 0, currency, locale)}
    </TabularFigures>
  );
};

export const Number = ({
  value,
  locale = "en-GB",
  shortFormat = false,
  className,
}: {
  value?: number | null;
  locale?: string;
  shortFormat?: boolean;
  className?: string;
}) => {
  return (
    <TabularFigures className={className}>
      {shortFormat
        ? numberFormatShort(value !== undefined ? value : 0, locale)
        : numberFormatLong(value ?? 0, locale)}
    </TabularFigures>
  );
};
